import styled from "@emotion/styled";
import { IconArrowLeft } from 'kompare-ui-library';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import aircash from "src/assets/images/Aircash.png";
import keks from "src/assets/images/keks.png";
import { HealthOfferPaymentOptionEnum, i18n, openCallMeModal, processQueryResult, setOfferId, setOneTimeToggle, setProgressBarPercentage, useAppDispatch, useAppSelector, useCheckoutHealthOfferPaymentOptionsQuery } from 'src/common';
import { Accordion, AccountPaymentDataCard, ArrowRightIcon, Button, Card, Flex, Installments, Loader, OfferFinalCard, RadioButtonGroup, RequestAgentCard, Text, WsPayIframe, ArrowDownIcon } from 'src/components';
import config from "src/config";
import { colors } from 'src/theme';
import { AppRoutes } from '../_router/app.routes';

const TextStyle = styled(Text)({
  marginBottom: '20px'
});

const TextStyle2 = styled(Text)({
  marginBottom: '12px'
});

const RadioButtonGroupStyle = styled(RadioButtonGroup)({
  marginBottom: '16px'
});

const LogoStyle = styled.img({
  height: '30px'
});

const ButtonContainerStyle = styled(Flex)({
  marginTop: 48,
  marginBottom: 40
});

const CardStyle = styled(Card)({
  padding: "8px 16px",
  textAlign: 'center'
});

// Provjera je li voucher validan
const hasValidVoucher = (modifier: any) => {
  if (!modifier) return false;
  if (typeof modifier.amount !== 'number') return false;
  if (modifier.amount === 0) return false;
  if (!modifier.type) return false;
  if (!['fixed', 'percentage'].includes(modifier.type)) return false;
  if (modifier.type === 'percentage' && (modifier.amount < 0 || modifier.amount > 100)) return false;
  return true;
};

const CheckoutPage = () => {
  const dispatch = useAppDispatch();
  const params = useParams<{ offerId: string }>();
  const navigate = useNavigate();

  const offerId = params.offerId as string;

  const oneTimeToggle = useAppSelector((store) => store.model.offer.oneTimeToggle);
  // const [isYearToggleChecked, setIsYearToggleChecked] = useState(oneTimeToggle);
  const [openPaymentMethodIndex, setOpenPaymentMethodIndex] = useState<number | null>(null);
  const [selectedPaymentType, setSelectedPaymentType] = useState<'oneTime' | 'installments'>(oneTimeToggle ? 'oneTime' : 'installments');

  useEffect(() => {
    dispatch(setProgressBarPercentage(80));
  }, []);

  useEffect(() => {
    dispatch(setOfferId(offerId));
  }, [offerId]);

  const { loading, data } = useCheckoutHealthOfferPaymentOptionsQuery({  // Treba poslati 12 i 1
    variables: { offerId },
    fetchPolicy: 'cache-first',
    onCompleted: (result) => {
      processQueryResult(result);
    }
  });

  // Destrukturiranje podataka
  const { health } = data || {};
  const { checkoutHealthOffer, checkoutHealthOfferGeneralTransferDetailsOneTime, checkoutHealthOfferGeneralTransferDetailsInstallments, checkoutHealthOfferPaymentOptions } = health || {};


  useEffect(() => {
    if (!checkoutHealthOffer) return;

    if (hasValidVoucher(checkoutHealthOffer?.supplementary?.voucherPriceChangeModifier)) {
      setSelectedPaymentType('oneTime');
      dispatch(setOneTimeToggle(true));
    }
  }, [checkoutHealthOffer]);

  if (!offerId) return null;
  if (loading) return <Loader page />;

  if (!checkoutHealthOfferPaymentOptions) return null;
  const paymentOptions = [
    {
      key: 'creditCard',
      type: 'oneTime',
      title: i18n.t('pages.checkoutPage.onetimeCard'),
      icon: null,
      content: <WsPayIframe offerId={offerId!} paymentOption={HealthOfferPaymentOptionEnum.CreditCard} paymentType='visa' />
    },
    {
      key: 'generalTransfer',
      type: 'oneTime',
      title: i18n.t('pages.checkoutPage.onetimeAccount'),
      icon: null,
      content: <AccountPaymentDataCard data={checkoutHealthOfferGeneralTransferDetailsOneTime} type='oneTime' offerId={offerId} />,
    },
    {
      key: 'keksPay',
      type: 'oneTime',
      title: i18n.t('pages.checkoutPage.keksPay'),
      icon: <LogoStyle src={keks} alt="" />,
      content: config.REACT_APP_IS_DEV === 'true' ? 'content' : <WsPayIframe offerId={offerId} paymentOption={HealthOfferPaymentOptionEnum.KeksPay} paymentType='keksPay' />,
    },
    {
      key: 'aircash',
      type: 'oneTime',
      title: i18n.t('pages.checkoutPage.aircash'),
      icon: <LogoStyle src={aircash} alt="" />,
      content: config.REACT_APP_IS_DEV === 'true' ? 'content' : <WsPayIframe offerId={offerId} paymentOption={HealthOfferPaymentOptionEnum.Aircash} paymentType='aircash' />,
    },
    {
      key: 'creditCardWithInstallments',
      type: 'installments',
      title: i18n.t('pages.checkoutPage.installmentsCard'),
      icon: null,
      content: (<Flex vertical gap={24}>
        <Flex gap={20}>
          <ArrowRightIcon color={colors.icon1} />
          <Installments data={checkoutHealthOfferPaymentOptions}></Installments>
        </Flex>
        <CardStyle radius={8} background={colors.primary5}><Text type='small'>{i18n.t('pages.checkoutPage.installmentsCardDescription')}</Text></CardStyle>
        <WsPayIframe offerId={offerId!} paymentOption={HealthOfferPaymentOptionEnum.CreditCard} paymentType='visa' installments />
      </Flex>)
    },
    {
      key: 'generalTransferWithInstallments',
      type: 'installments',
      title: i18n.t('pages.checkoutPage.installmentsAccount'),
      icon: null,
      content: <AccountPaymentDataCard data={checkoutHealthOfferGeneralTransferDetailsInstallments} type='installments' offerId={offerId} />,
    }
  ].filter(pm => {
    // Prvo provjerimo postoji li opcija plaćanja u paymentOptions
    const hasPaymentOption = checkoutHealthOfferPaymentOptions!.paymentOptions!
      .find((po: any) => po.paymentOption === pm.key)!.paymentTypes!.length > 0;

    // Ako postoji validan voucher, filtriraj samo jednokratne opcije plaćanja
    if (hasValidVoucher(checkoutHealthOffer?.supplementary?.voucherPriceChangeModifier)) {
      return hasPaymentOption && pm.type === 'oneTime';
    }

    return hasPaymentOption;
  });
  // ].filter(pm => checkoutHealthOfferPaymentOptions!.paymentOptions!.find((po: any) => po.paymentOption === pm.key)!.paymentTypes!.length > 0);

  const handleChangePaymentType = (value: 'oneTime' | 'installments') => {
    setSelectedPaymentType(value);
    if (value === 'oneTime') {
      dispatch(setOneTimeToggle(true));
      // setIsYearToggleChecked(true);
    } else {
      dispatch(setOneTimeToggle(false));
      // setIsYearToggleChecked(false);
    }
  };

  const handleYearToggle = (checked: boolean) => {
    // setIsYearToggleChecked(checked);
    dispatch(setOneTimeToggle(checked));
    if (checked) {
      setSelectedPaymentType('oneTime');
    } else {
      setSelectedPaymentType('installments');
    }
  }

  const handleOpenPaymentMethod = (index: number) => {
    setOpenPaymentMethodIndex(openPaymentMethodIndex === index ? null : index);
  };

  const handleBackButton = () => {
    navigate(`${AppRoutes.OFFER.fullPath}/${offerId}`);
  }

  return (
    <Flex vertical gap={32}>
      <Flex vertical gap={12}>
        {/* <VoucherCodeCard offerId={offerId} appliedVoucherCode={checkoutHealthOffer?.supplementary?.voucherPriceChangeModifier?.code} /> */}
        <Text type="xLarge" bold>{i18n.t('pages.checkoutPage.purchaseSubject')}</Text>
        <OfferFinalCard offer={checkoutHealthOffer} handleYearToggle={() => handleYearToggle(!oneTimeToggle)} yearToggle={oneTimeToggle} yearToggleDisabled={hasValidVoucher(checkoutHealthOffer?.supplementary?.voucherPriceChangeModifier)} />
      </Flex>
      <div>
        <TextStyle type='xLarge' bold>{i18n.t('pages.checkoutPage.paymentMethod')}</TextStyle>
        <TextStyle2 type='tiny'>{i18n.t('pages.checkoutPage.paymentMethodDescription')}</TextStyle2>
        <RadioButtonGroupStyle
          options={[
            { value: 'oneTime', label: i18n.t('pages.checkoutPage.onetime') },
            ...(hasValidVoucher(checkoutHealthOffer?.supplementary?.voucherPriceChangeModifier)
              ? []
              : [{ value: 'installments', label: i18n.t('pages.checkoutPage.installments') }]
            )
          ]}
          onChange={(value) => handleChangePaymentType(value as 'oneTime' | 'installments')}
          value={selectedPaymentType}
        />
        <Flex vertical gap={12}>
          {paymentOptions.map((pm, index) => {
            if (pm.type === selectedPaymentType)
              return (
                <Card padding={16} radius={12} key={index}>
                  <Accordion
                    key={index}
                    title={<Flex gap={16} align='center'>{pm.icon}<Text type='base' bold>{pm.title}</Text></Flex>}
                    content={pm.content}
                    open={openPaymentMethodIndex === index}
                    expandable={true}
                    expandIcon={true}
                    expandIconComponent={<ArrowDownIcon color={colors.icon1} />}
                    gap={20}
                    onChange={() => handleOpenPaymentMethod(index)}
                  />
                </Card>
              )
            return null;
          })}

        </Flex>
        <ButtonContainerStyle justify="center" align="center">
          <Button btnType="textBlack" iconPosition="left" text={i18n.t('pages.offerPage.backButton')} onClick={handleBackButton}>{<IconArrowLeft />}</Button>
        </ButtonContainerStyle>
        <Flex>
          <RequestAgentCard onButtonClick={() => dispatch(openCallMeModal())} />
        </Flex>
      </div>
    </Flex>
  );
}

export default CheckoutPage;
